import { Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ContractStatus } from './ContractStatusBadge';

export interface ContractFilterState {
  status: ContractStatus | 'all';
  contractType: string;
  search: string;
}

interface ContractFiltersProps {
  filters: ContractFilterState;
  onChange: (filters: ContractFilterState) => void;
  className?: string;
}

const STATUS_OPTIONS: { value: ContractStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'active', label: 'Active' },
  { value: 'expiring', label: 'Expiring' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' },
  { value: 'archived', label: 'Archived' },
];

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'nda', label: 'NDA' },
  { value: 'service_agreement', label: 'Service Agreement' },
  { value: 'employment', label: 'Employment' },
  { value: 'lease', label: 'Lease' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FILTERS: ContractFilterState = { status: 'all', contractType: 'all', search: '' };

function hasActiveFilters(filters: ContractFilterState): boolean {
  return filters.status !== 'all' || filters.contractType !== 'all' || filters.search !== '';
}

export function ContractFilters({ filters, onChange, className }: ContractFiltersProps) {
  function handleStatusChange(e: React.ChangeEvent<HTMLSelectElement>) {
    onChange({ ...filters, status: e.target.value as ContractStatus | 'all' });
  }

  function handleTypeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    onChange({ ...filters, contractType: e.target.value });
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange({ ...filters, search: e.target.value });
  }

  function handleClear() {
    onChange(EMPTY_FILTERS);
  }

  const selectClass =
    'h-9 rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

  return (
    <div
      role="search"
      aria-label="Contract filters"
      className={cn('flex flex-wrap items-center gap-3', className)}
    >
      <div className="relative flex-1 min-w-[220px]">
        <Search
          className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none"
          aria-hidden="true"
        />
        <input
          type="search"
          placeholder="Search by title, contract number, or vendor..."
          value={filters.search}
          onChange={handleSearchChange}
          aria-label="Search contracts"
          className={cn(
            'pl-8 w-full h-9 rounded-md border border-input bg-background px-3 py-1 text-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        />
      </div>

      <label htmlFor="filter-status" className="sr-only">Status</label>
      <select
        id="filter-status"
        value={filters.status}
        onChange={handleStatusChange}
        className={selectClass}
        aria-label="Filter by status"
      >
        {STATUS_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <label htmlFor="filter-type" className="sr-only">Contract type</label>
      <select
        id="filter-type"
        value={filters.contractType}
        onChange={handleTypeChange}
        className={selectClass}
        aria-label="Filter by contract type"
      >
        {TYPE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      {hasActiveFilters(filters) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClear}
          aria-label="Clear all filters"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4 mr-1" aria-hidden="true" />
          Clear filters
        </Button>
      )}
    </div>
  );
}
